import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase-client';
import Sidebar from '@/components/Sidebar';
import DashboardHeader from '@/components/DashboardHeader';
import CreditBalance from '@/components/CreditBalance';
import SubscriptionPlans from '@/components/SubscriptionPlans';
import CreditPackages from '@/components/CreditPackages';
import CreditTransactions from '@/components/CreditTransactions';

interface BillingInfo {
  credits: {
    balance: number;
    total_earned: number;
    total_spent: number;
  };
  subscription: any;
  plans: any[];
  packages: any[];
  transactions: any[];
}

export default function BillingPage() {
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://eutekssvzhtkwzperkzb.supabase.co/functions/v1';

  useEffect(() => {
    loadBillingInfo();
    
    // Check for success/canceled parameter and reload billing info
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true' || urlParams.get('canceled') === 'true') {
      // Wait a bit for webhook to process, then reload
      setTimeout(() => {
        loadBillingInfo();
      }, 2000);
      
      // Clean up URL
      window.history.replaceState({}, '', '/dashboard/billing');
    }
  }, []);

  const loadBillingInfo = async () => {
    try {
      // Get current session
      let { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        setLoading(false);
        return;
      }

      // Check if token is expired (within 5 minutes) and refresh if needed
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      if (expiresAt && expiresAt - now < 300) { // Refresh if expires in less than 5 minutes
        const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
        if (refreshedSession) {
          session = refreshedSession;
        }
      }

      if (!session?.access_token) {
        setLoading(false);
        return;
      }

      // Get Supabase anon key for apikey header
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

      if (!supabaseAnonKey) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${apiBaseUrl}/get-billing-info`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBillingInfo(data);
      } else {
        // If API fails, set default values so page still shows
        await response.text();
        setBillingInfo({
          credits: { balance: 0, total_earned: 0, total_spent: 0 },
          subscription: null,
          plans: [],
          packages: [],
          transactions: [],
        });
      }
    } catch (error) {
      // Set default values on error
      setBillingInfo({
        credits: { balance: 0, total_earned: 0, total_spent: 0 },
        subscription: null,
        plans: [],
        packages: [],
        transactions: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (priceId: string, type: 'subscription' | 'one_time') => {
    try {
      // If user has an active subscription and is trying to subscribe again, show confirmation
      if (type === 'subscription' && billingInfo?.subscription) {
        const currentPlan = billingInfo.subscription.subscription_plans?.name || 'your current plan';
        const confirmed = window.confirm(
          `You currently have an active subscription (${currentPlan}).\n\n` +
          `If you proceed, Stripe will:\n` +
          `• Upgrade or downgrade your plan immediately\n` +
          `• Prorate the charges based on your billing cycle\n` +
          `• Update your subscription automatically\n\n` +
          `Do you want to continue?`
        );
        
        if (!confirmed) {
          return;
        }
      }

      // Get current session
      let { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        alert('Please log in to continue');
        return;
      }

      // Check if token is expired (within 5 minutes) and refresh if needed
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      if (expiresAt && expiresAt - now < 300) { // Refresh if expires in less than 5 minutes
        const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
        if (refreshedSession) {
          session = refreshedSession;
        }
      }

      if (!session?.access_token) {
        alert('Session expired. Please log in again.');
        return;
      }

      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

      if (!supabaseAnonKey) {
        return;
      }

      const response = await fetch(`${apiBaseUrl}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ price_id: priceId, type }),
      });

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        await response.text();
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  const handleManageSubscription = async () => {
    try {
      // Get current session
      let { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        alert('Please log in to continue');
        return;
      }

      // Check if token is expired (within 5 minutes) and refresh if needed
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      if (expiresAt && expiresAt - now < 300) { // Refresh if expires in less than 5 minutes
        const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
        if (refreshedSession) {
          session = refreshedSession;
        }
      }

      if (!session?.access_token) {
        alert('Session expired. Please log in again.');
        return;
      }

      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

      if (!supabaseAnonKey) {
        return;
      }

      const response = await fetch(`${apiBaseUrl}/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const { url } = await response.json();
        // Open portal - when user returns, the page will auto-refresh
        window.location.href = url;
      } else {
        await response.text();
        
        // Check if it's because there's no active subscription
        if (response.status === 404) {
          alert('No active subscription found. Please subscribe to a plan first.');
        } else {
          alert('Failed to open subscription management. Please try again.');
        }
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors">
      <Sidebar />

      <div className="lg:pl-64">
        <DashboardHeader title="Billing & Credits" />

        <main className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Credit Balance - Always show */}
          <div className="mb-8">
            <CreditBalance 
              credits={billingInfo?.credits || { balance: 0, total_earned: 0, total_spent: 0 }} 
            />
          </div>

          {/* Current Subscription */}
          {billingInfo?.subscription ? (
            <div className="mb-8 glass-card rounded-xl p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Current Subscription
                    </h3>
                    {billingInfo.subscription.status === 'canceled' && (
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-semibold rounded">
                        Canceled
                      </span>
                    )}
                    {billingInfo.subscription.status === 'past_due' && (
                      <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-semibold rounded">
                        Past Due
                      </span>
                    )}
                    {billingInfo.subscription.status === 'trialing' && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-semibold rounded">
                        Trial
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <span className="font-semibold">{billingInfo.subscription.subscription_plans?.name || 'Active Plan'}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {billingInfo.subscription.subscription_plans?.credits_per_period || 0} credits per{' '}
                    {billingInfo.subscription.billing_cycle}
                  </p>
                  {billingInfo.subscription.cancel_at_period_end && (
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-2 font-medium">
                      ⚠️ Subscription will end on {new Date(billingInfo.subscription.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                  {billingInfo.subscription.status === 'canceled' && !billingInfo.subscription.cancel_at_period_end && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-medium">
                      Subscription has been canceled. You can still use your remaining credits.
                    </p>
                  )}
                  {billingInfo.subscription.status === 'past_due' && (
                    <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2 font-medium">
                      ⚠️ Payment failed. Please update your payment method to continue.
                    </p>
                  )}
                </div>
                <button
                  onClick={handleManageSubscription}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition ml-4"
                >
                  Manage Subscription
                </button>
              </div>
            </div>
          ) : (
            <div className="mb-8 glass-card rounded-xl p-6">
              <div className="text-center py-4">
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  No active subscription
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  You're currently on the Free plan with 50 credits
                </p>
              </div>
            </div>
          )}

          {/* Subscription Plans - Always show if available */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Subscription Plans
            </h3>
            {billingInfo?.plans && billingInfo.plans.length > 0 ? (
              <SubscriptionPlans
                plans={billingInfo.plans}
                currentSubscription={billingInfo.subscription}
                onSelectPlan={handleCheckout}
              />
            ) : (
              <div className="glass-card rounded-xl p-6 text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  Loading subscription plans...
                </p>
              </div>
            )}
          </div>

          {/* Credit Packages - Always show if available */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              One-Time Credit Purchase
            </h3>
            {billingInfo?.packages && billingInfo.packages.length > 0 ? (
              <CreditPackages
                packages={billingInfo.packages}
                onSelectPackage={handleCheckout}
              />
            ) : (
              <div className="glass-card rounded-xl p-6 text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  Loading credit packages...
                </p>
              </div>
            )}
          </div>

          {/* Recent Transactions */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Recent Transactions
            </h3>
            {billingInfo?.transactions && billingInfo.transactions.length > 0 ? (
              <CreditTransactions transactions={billingInfo.transactions} />
            ) : (
              <div className="glass-card rounded-xl p-6 text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  No transactions yet
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                  Your transaction history will appear here
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

